import { lazy, type ComponentType } from 'react';

/** Shared key — also checked by RouteErrorBoundary to avoid double-reload */
export const CHUNK_RELOAD_KEY = 'chunk_page_reload';
const RELOAD_COOLDOWN_MS = 10_000; // 10s

function isChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

/**
 * Drop-in replacement for React.lazy that handles stale-deployment chunk errors.
 *
 * 1. Retry the import once after 1.5 s (handles transient network blips)
 * 2. If still failing, force a page reload so the browser fetches fresh HTML
 *    with new chunk references
 * 3. Uses sessionStorage to prevent infinite reload loops — if we already
 *    reloaded within the cooldown window, the error propagates to the
 *    RouteErrorBoundary which shows a manual-reload UI
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
): ReturnType<typeof lazy> {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (firstError) {
      if (!isChunkError(firstError)) throw firstError;

      // Retry once after a short delay (transient network issue)
      await new Promise(r => setTimeout(r, 1500));

      try {
        return await importFn();
      } catch (retryError) {
        if (!isChunkError(retryError)) throw retryError;

        // Genuine stale chunk — check if we already reloaded recently
        const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
        if (lastReload && Date.now() - Number(lastReload) < RELOAD_COOLDOWN_MS) {
          // Already reloaded — let error bubble to RouteErrorBoundary
          console.warn('[lazyWithRetry] Already reloaded recently, showing error UI');
          throw retryError;
        }

        // Record this reload
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        console.warn('[lazyWithRetry] Stale chunk detected, reloading page…');

        // Unregister service workers so they don't serve stale HTML
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
        // Clear all SW caches so the SW can't serve stale assets after re-registration
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }

        // Cache-busting reload — bypasses browser disk cache and CDN edge cache
        // (simple reload() can still serve old index.html from cache, causing a loop)
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', String(Date.now()));
        window.location.replace(url.toString());

        // Never-resolving promise prevents React from rendering while reloading
        return new Promise<never>(() => {});
      }
    }
  });
}
